const DEFAULT_ACCOUNT_LIBRARY_CACHE_TTL_MS = 60_000;

type CachePredicate = (key: string) => boolean;

interface CacheEntry<T> {
  expiresAt: number;
  value: T;
}

interface CachedFetchOptions {
  force?: boolean;
  ttlMs?: number;
}

interface CacheUserShape {
  auth0Sub?: string | null;
  email?: string | null;
  id?: string | null;
  sub?: string | null;
}

const cache = new Map<string, CacheEntry<unknown>>();
const inFlight = new Map<string, Promise<unknown>>();

export const ACCOUNT_LIBRARY_CACHE_TTL_MS = DEFAULT_ACCOUNT_LIBRARY_CACHE_TTL_MS;
export const ACCOUNT_LIBRARY_BOTS_CACHE_TTL_MS = 10_000;

export function getAccountLibraryUserCacheKey(
  user?: CacheUserShape | null,
  userProfile?: CacheUserShape | null,
): string {
  const source = userProfile || user;
  return source?.id || source?.sub || source?.auth0Sub || source?.email || 'anonymous';
}

export function buildAccountLibraryCacheKey(
  userKey: string,
  resource: string,
  params: URLSearchParams | string,
): string {
  const query = typeof params === 'string' ? params : params.toString();
  return `account-library:${userKey}:${resource}:${query}`;
}

export function getCachedAccountLibraryData<T>(key: string): T | null {
  const entry = cache.get(key);
  if (!entry) return null;

  if (entry.expiresAt <= Date.now()) {
    cache.delete(key);
    return null;
  }

  return entry.value as T;
}

export function setCachedAccountLibraryData<T>(
  key: string,
  value: T,
  ttlMs = DEFAULT_ACCOUNT_LIBRARY_CACHE_TTL_MS,
): void {
  cache.set(key, {
    expiresAt: Date.now() + ttlMs,
    value,
  });
}

export function clearAccountLibraryCache(predicate?: CachePredicate): void {
  if (!predicate) {
    cache.clear();
    inFlight.clear();
    return;
  }

  Array.from(cache.keys()).forEach((key) => {
    if (predicate(key)) cache.delete(key);
  });
  Array.from(inFlight.keys()).forEach((key) => {
    if (predicate(key)) inFlight.delete(key);
  });
}

export async function fetchAccountLibraryCached<T>(
  key: string,
  fetcher: () => Promise<T>,
  options: CachedFetchOptions = {},
): Promise<T> {
  const { force = false, ttlMs = DEFAULT_ACCOUNT_LIBRARY_CACHE_TTL_MS } = options;

  if (!force) {
    const cached = getCachedAccountLibraryData<T>(key);
    if (cached) return cached;

    const pending = inFlight.get(key) as Promise<T> | undefined;
    if (pending) return pending;
  }

  const request = fetcher().then((value) => {
    setCachedAccountLibraryData(key, value, ttlMs);
    return value;
  });

  inFlight.set(key, request);

  try {
    return await request;
  } finally {
    if (inFlight.get(key) === request) {
      inFlight.delete(key);
    }
  }
}
